import { useMemo } from 'react';

// Premium Community Loading - Leaves falling INTO centered jar
export const CommunityLoadingAnimation = () => {
  // Generate leaves that fall into the center of the jar
  const leaves = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      id: i,
      delay: i * 0.4,
      duration: 2.5 + Math.random() * 0.8,
      // Start spread - all converge to jar center (x=0)
      startX: (Math.random() - 0.5) * 50, // -25 to +25 px from center
      size: 0.55 + Math.random() * 0.35,
      rotation: Math.random() * 360,
      leafType: i % 3
    }));
  }, []);

  const leafPaths = [
    "M12 2C8 2 5 6 5 10C5 14 8 18 12 22C16 18 19 14 19 10C19 6 16 2 12 2Z",
    "M12 3C7 3 3 8 3 12C3 16 6 20 12 22C18 20 21 16 21 12C21 8 17 3 12 3Z",
    "M12 1C9 1 7 5 7 10C7 15 9 19 12 23C15 19 17 15 17 10C17 5 15 1 12 1Z"
  ];

  return (
    <div className="flex flex-col items-center justify-center py-8">
      <style>{`
        @keyframes leafFallIntoJar {
          0% {
            transform: translateY(-20px) translateX(var(--start-x)) rotate(0deg) scale(var(--scale));
            opacity: 0;
          }
          10% {
            opacity: 0.9;
          }
          30% {
            transform: translateY(40px) translateX(calc(var(--start-x) * 0.6)) rotate(120deg) scale(var(--scale));
          }
          60% {
            transform: translateY(90px) translateX(calc(var(--start-x) * 0.2)) rotate(240deg) scale(var(--scale));
          }
          85% {
            transform: translateY(125px) translateX(0px) rotate(320deg) scale(calc(var(--scale) * 0.8));
            opacity: 0.7;
          }
          100% {
            transform: translateY(140px) translateX(0px) rotate(360deg) scale(calc(var(--scale) * 0.5));
            opacity: 0;
          }
        }
        
        .leaf-fall {
          animation: leafFallIntoJar var(--duration) ease-in infinite;
          animation-delay: var(--delay);
        }
        
        @keyframes jarGlow {
          0%, 100% { filter: drop-shadow(0 4px 12px rgba(139, 90, 43, 0.15)); }
          50% { filter: drop-shadow(0 6px 18px rgba(139, 90, 43, 0.22)); }
        }
        
        .jar-glow {
          animation: jarGlow 3s ease-in-out infinite;
        }
        
        @keyframes creamWobble {
          0%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(1.02); }
        }
        
        .cream-wobble {
          animation: creamWobble 2.5s ease-in-out infinite;
          transform-origin: bottom;
        }
        
        @keyframes dotsMove {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>
      
      {/* Container - everything centered */}
      <div className="relative flex flex-col items-center" style={{ width: '160px', height: '200px' }}>
        
        {/* Leaves container - centered above jar */}
        <div 
          className="absolute overflow-visible pointer-events-none"
          style={{ 
            top: '0px', 
            left: '50%', 
            transform: 'translateX(-50%)',
            width: '100px',
            height: '160px',
            zIndex: 20 
          }}
        >
          {leaves.map((leaf) => (
            <div
              key={leaf.id}
              className="absolute leaf-fall"
              style={{
                left: '50%',
                top: '0',
                marginLeft: '-10px',
                '--delay': `${leaf.delay}s`,
                '--duration': `${leaf.duration}s`,
                '--start-x': `${leaf.startX}px`,
                '--scale': leaf.size
              }}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <path 
                  d={leafPaths[leaf.leafType]} 
                  fill="#5D8A4C"
                  fillOpacity="0.9"
                />
                <path 
                  d="M12 5 L12 18" 
                  stroke="#3D6B32" 
                  strokeWidth="0.7" 
                  strokeOpacity="0.4"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          ))}
        </div>
        
        {/* Jar - centered at bottom */}
        <div 
          className="absolute jar-glow"
          style={{ 
            bottom: '0', 
            left: '50%', 
            transform: 'translateX(-50%)',
            width: '100px',
            height: '130px'
          }}
        >
          <svg viewBox="0 0 100 130" className="w-full h-full">
            <defs>
              {/* Amber glass */}
              <linearGradient id="amberGlass3" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#8B5A2B" stopOpacity="0.35"/>
                <stop offset="30%" stopColor="#CD853F" stopOpacity="0.2"/>
                <stop offset="50%" stopColor="#DEB887" stopOpacity="0.15"/>
                <stop offset="70%" stopColor="#CD853F" stopOpacity="0.2"/>
                <stop offset="100%" stopColor="#8B5A2B" stopOpacity="0.35"/>
              </linearGradient>
              
              {/* Cream fill */}
              <linearGradient id="creamGrad3" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FDFCFA"/>
                <stop offset="50%" stopColor="#F8F5F0"/>
                <stop offset="100%" stopColor="#F0EDE6"/>
              </linearGradient>
              
              {/* Jar interior clip */}
              <clipPath id="jarInterior3">
                <path d="M15 35 Q15 28 28 28 L72 28 Q85 28 85 35 L85 105 Q85 118 50 118 Q15 118 15 105 Z"/>
              </clipPath>
            </defs>
            
            {/* Shadow */}
            <ellipse cx="50" cy="122" rx="28" ry="5" fill="#2C3329" fillOpacity="0.08"/>
            
            {/* Jar body */}
            <path 
              d="M15 35 Q15 28 28 28 L72 28 Q85 28 85 35 L85 105 Q85 118 50 118 Q15 118 15 105 Z"
              fill="url(#amberGlass3)"
              stroke="#8B5A2B"
              strokeWidth="1.2"
              strokeOpacity="0.4"
            />
            
            {/* Cream inside */}
            <g clipPath="url(#jarInterior3)" className="cream-wobble">
              {/* Base cream */}
              <rect x="15" y="58" width="70" height="60" fill="url(#creamGrad3)"/>
              
              {/* Wavy top */}
              <path 
                d="M15 60 Q32 56 50 60 Q68 64 85 60 L85 65 Q68 61 50 65 Q32 61 15 65 Z"
                fill="#FDFCFA"
              />
              
              {/* Settled leaves hints */}
              <ellipse cx="35" cy="66" rx="5" ry="2.5" fill="#6B9B5A" fillOpacity="0.2" transform="rotate(-15 35 66)"/>
              <ellipse cx="58" cy="64" rx="4" ry="2" fill="#5D8A4C" fillOpacity="0.15" transform="rotate(10 58 64)"/>
              <ellipse cx="48" cy="68" rx="3" ry="1.5" fill="#7DAA6B" fillOpacity="0.12" transform="rotate(-5 48 68)"/>
            </g>
            
            {/* Jar neck */}
            <rect x="28" y="18" width="44" height="12" rx="3" fill="url(#amberGlass3)" stroke="#8B5A2B" strokeWidth="0.8" strokeOpacity="0.35"/>
            
            {/* Lid */}
            <rect x="23" y="8" width="54" height="12" rx="4" fill="#3A4A35"/>
            <rect x="27" y="10" width="46" height="8" rx="2" fill="#4A5D43"/>
            <rect x="30" y="12" width="18" height="2.5" rx="1" fill="#5D7356" fillOpacity="0.45"/>
            
            {/* Glass shine */}
            <path 
              d="M22 40 Q20 36 24 34 L29 34 Q33 36 31 40 L31 95 Q33 100 29 104 L24 104 Q20 100 22 95 Z"
              fill="white"
              fillOpacity="0.12"
            />
          </svg>
        </div>
      </div>
      
      {/* Loading text - centered */}
      <div className="text-center mt-3">
        <p className="text-[#4A5D43] font-medium text-sm">
          טוען ניסויים מהקהילה...
        </p>
        <div className="flex justify-center gap-1.5 mt-2">
          <span className="w-1.5 h-1.5 bg-[#4A5D43] rounded-full animate-bounce" style={{ animationDelay: '0s' }}></span>
          <span className="w-1.5 h-1.5 bg-[#4A5D43] rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></span>
          <span className="w-1.5 h-1.5 bg-[#4A5D43] rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></span>
        </div>
      </div>
    </div>
  );
};

export default CommunityLoadingAnimation;
